import React, { useState } from 'react';
import { User, Edit, Mail, Sparkles, Heart, ThumbsUp, MessageSquare, Bell } from 'lucide-react';
import EditProfileModal from './EditProfileModal';

const SORA = "'Sora', system-ui, sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";

const ProfileHeader = ({ user, onProfileUpdate, isMobile = false, summary = null }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const userData = user || {
    username: 'Guest User',
    avatar: null,
    tier: 'free',
    email: 'guest@reactorhub.com',
    bio: '',
  };

  const handleProfileSave = (updatedUser) => {
    onProfileUpdate && onProfileUpdate(updatedUser);
  };

  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1.4fr) minmax(260px, 0.8fr)',
      gap: isMobile ? '16px' : '20px',
      padding: isMobile ? '18px' : '22px 24px',
      background: 'linear-gradient(120deg, #3b1673 0%, #581c5c 55%, #7e1450 100%)',
      borderRadius: isMobile ? '18px' : '22px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      marginBottom: isMobile ? '14px' : '18px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.28)',
      boxSizing: 'border-box',
      fontFamily: MANROPE,
    }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 85% 15%, rgba(255,255,255,0.08), transparent 55%)',
        pointerEvents: 'none'
      }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', width: '100%', minWidth: 0, position: 'relative', zIndex: 1 }}>
        <div style={{
          width: isMobile ? '56px' : '72px',
          height: isMobile ? '56px' : '72px',
          borderRadius: '20px',
          background: '#8a5a3c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          border: '3px solid rgba(255,255,255,0.25)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.28)',
          flexShrink: 0
        }}>
          {userData.avatar ? (
            <img src={userData.avatar} alt={userData.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <User size={isMobile ? 26 : 32} color="#D1D5DB" />
          )}
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
            <span style={{
              color: '#FFFFFF',
              fontSize: isMobile ? '22px' : '26px',
              fontWeight: '800',
              lineHeight: 1.1,
              minWidth: 0,
              fontFamily: SORA,
            }}>
              {userData.username}
            </span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '4px 10px',
              borderRadius: '999px',
              background: 'rgba(255,255,255,0.16)',
              color: '#FFFFFF',
              fontSize: '11.5px',
              fontWeight: '700',
              letterSpacing: '0.03em',
              textTransform: 'uppercase'
            }}>
              <Sparkles size={12} />
              My Space
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px', color: '#9CA3AF', minWidth: 0 }}>
            <Mail size={13} />
            <span style={{
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {userData.email}
            </span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px',
            marginBottom: userData.bio ? '12px' : 0,
          }}>
            {[
              { icon: Heart, label: 'Favorites', value: summary?.favorites ?? 0 },
              { icon: ThumbsUp, label: 'Votes', value: summary?.votes ?? 0 },
              { icon: MessageSquare, label: 'Threads', value: summary?.threads ?? 0 },
              { icon: Bell, label: 'Unread', value: summary?.unread ?? 0 },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <item.icon size={13} color="rgba(255,255,255,0.65)" />
                <span style={{ fontSize: '13px', fontWeight: '800', color: '#FFFFFF', fontFamily: SORA }}>
                  {item.value}
                </span>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)' }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          {userData.bio && (
            <p style={{
              color: '#D1D5DB',
              fontSize: '13px',
              lineHeight: '1.55',
              margin: 0,
              maxWidth: isMobile ? '100%' : '440px',
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 3 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {userData.bio}
            </p>
          )}
        </div>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isMobile ? 'flex-start' : 'flex-start',
        alignItems: isMobile ? 'stretch' : 'flex-end',
        minWidth: 0,
        position: 'relative',
        zIndex: 1
      }}>
        <button
          onClick={() => setShowEditModal(true)}
          style={{
            background: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '12px',
            padding: '11px 16px',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '700',
            fontFamily: MANROPE,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            transition: 'background 0.15s ease',
            width: isMobile ? '100%' : 'fit-content',
            justifyContent: 'center',
            alignSelf: isMobile ? 'stretch' : 'flex-end'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.22)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)'}
        >
          <Edit size={14} />
          Edit Profile
        </button>
      </div>

      {showEditModal && (
        <EditProfileModal user={userData} onClose={() => setShowEditModal(false)} onSave={handleProfileSave} />
      )}
    </div>
  );
};

export default ProfileHeader;
